// package imports
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

// CSS imports
import './UserRequests.css';

// Component imports
import Spinner from '../../components/spinner/Spinner';
import RequestUserCard from '../../components/requestUserCard/RequestUserCard';
import Alert from '../../components/alert/Alert';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { UserRequest } from '../../types/user/UserRequest';
import { SectionContentSize } from '../../types/sectionWrapper/SectionContentSize';
import { UserRequestsResult } from '../../types/user/UserRequestsResult';
import { AlertSize } from '../../types/alert/AlertSize';

const UserRequests: React.FC = () => {
  const [selectedPage, setSelectedPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [users, setUsers] = useState<UserRequest[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [showUnknownError, setShowUnknownError] = useState(false);

  const getUserRequests = useCallback(
    async (tempUsers: UserRequest[], page: number) => {
      setIsLoading(true);
      const res: UserRequestsResult | null = await ApiService.getUserRequests(
        page,
        9
      );
      setIsLoading(false);
      if (res) {
        for (const user of res.users) {
          tempUsers.push(user);
        }
        setUsers(tempUsers);
        setSelectedPage(res.page);
        setPagesCount(res.pages);
      }
    },
    []
  );

  useEffect(() => {
    getUserRequests([], 1);
  }, [getUserRequests]);

  const acceptUser = async (id: string, index: number, role: string) => {
    const res: boolean = await ApiService.acceptRequestUser(id, role);
    if (res) {
      const tempUsers = users.slice(0, users.length);
      tempUsers.splice(index, 1);
      setUsers(tempUsers);
    } else {
      setShowUnknownError(true);
    }
  };

  const declineUser = async (id: string, index: number) => {
    const res: boolean = await ApiService.declineRequestUser(id);
    if (res) {
      const tempUsers = users.slice(0, users.length);
      tempUsers.splice(index, 1);
      setUsers(tempUsers);
    } else {
      setShowUnknownError(true);
    }
  };

  return (
    <div className='UserRequests'>
      <SectionWrapper contentWidth={SectionContentSize.MEDIUM}>
        <div className='content'>
          <h2>User Requests</h2>
          <InfiniteScroll
            dataLength={users.length}
            hasMore={selectedPage !== pagesCount}
            next={() => getUserRequests(users, selectedPage + 1)}
            loader={<Spinner isLoading={true} />}
            className='infinite-scroll-container'
          >
            {users.map((user: UserRequest, index: number) => (
              <RequestUserCard
                className='item-card'
                user={user}
                index={index}
                acceptUser={acceptUser}
                declineUser={declineUser}
                key={index}
              />
            ))}
          </InfiniteScroll>
          {!isLoading && users.length === 0 ? (
            <span className='no-items-found'>No user requests</span>
          ) : null}
        </div>
      </SectionWrapper>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default UserRequests;
